import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Space, Typography } from "antd";
import { QRCodeSVG } from "qrcode.react";
import { confirmTotp, enrolTotp } from "../../../api/auth/mfaApi";
import { authFailure, authFailureMessage, fieldError, hasFieldError, isStepUpDeclined } from "../../../lib/http/authErrors";
import type { TotpEnrolment } from "../../../types/auth/mfa";
import OtpInput from "../../../components/auth/mfa/OtpInput";
import StepUpForm from "../../../components/auth/mfa/StepUpForm";
import { isOtp, normaliseOtp } from "../../../components/auth/mfa/codes";
import EnrolmentEmailCode from "./EnrolmentEmailCode";
import RecoveryCodesList from "./RecoveryCodesList";
import { needsFinishingStepUp } from "./securityModel";

const { Paragraph, Text } = Typography;

type Stage =
	| { kind: "starting" }
	| { kind: "scan"; enrolment: TotpEnrolment }
	| { kind: "codes"; codes: string[] }
	| { kind: "finish" }
	| { kind: "failed"; message: string };

interface TotpSetupProps {
	requireEmailCode: boolean;
	onDone: () => void;
	onCancel?: () => void;
}

const groupSecret = (secret: string): string => secret.replace(/(.{4})/g, "$1 ").trim();

// Enrol → scan → confirm → recovery codes (first factor only) → step up with the next code when the session needs it (contract §6.6).
const TotpSetup: React.FC<TotpSetupProps> = ({ requireEmailCode, onDone, onCancel }) => {
	const [stage, setStage] = useState<Stage>({ kind: "starting" });
	const [emailRequired, setEmailRequired] = useState(requireEmailCode);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [form] = Form.useForm<{ code: string; email_code?: string }>();
	const started = useRef(false);

	const start = async () => {
		setStage({ kind: "starting" });
		setError("");
		try {
			setStage({ kind: "scan", enrolment: await enrolTotp() });
		} catch (err) {
			setStage({ kind: "failed", message: isStepUpDeclined(err) ? "Confirm it's you to add a sign-in method." : authFailureMessage(err) });
		}
	};

	useEffect(() => {
		if (started.current) return;
		started.current = true;
		void start();
	}, []);

	const continueAfterCodes = () => {
		if (needsFinishingStepUp()) setStage({ kind: "finish" });
		else onDone();
	};

	const confirm = async (enrolment: TotpEnrolment, values: { code: string; email_code?: string }) => {
		setBusy(true);
		setError("");
		try {
			const result = await confirmTotp(enrolment.factor_id, normaliseOtp(values.code), emailRequired ? normaliseOtp(values.email_code ?? "") : undefined);
			if (result.recovery_codes && result.recovery_codes.length > 0) setStage({ kind: "codes", codes: result.recovery_codes });
			else continueAfterCodes();
		} catch (err) {
			const failure = authFailure(err);
			if (failure.status === 422 && hasFieldError(failure, "email_code")) {
				form.setFields([{ name: "email_code", errors: ["The email code is incorrect or has expired."] }]);
			} else if (failure.status === 422 && hasFieldError(failure, "code")) {
				const message = fieldError(failure, "code") ?? "That code does not match. Check your phone's clock and enter the current code.";
				// identity discards the pending factor after too many wrong codes; only a new enrolment can succeed.
				if (/too many attempts/i.test(message)) {
					form.resetFields(["code"]);
					void start();
					setError("Too many incorrect codes, so setup has started again. Scan the new QR code, then enter its code.");
				} else {
					form.setFields([{ name: "code", value: "", errors: [message] }]);
				}
			} else if (failure.code === "mfa_enrolment_required" && failure.details.reason === "email_code_required") {
				setEmailRequired(true);
				setError("Enter the code we email you to finish.");
			} else if (failure.status === 404) {
				setStage({ kind: "failed", message: "This setup expired. Start again." });
			} else if (isStepUpDeclined(err)) {
				setError("Confirm it's you to finish adding this method.");
			} else {
				setError(authFailureMessage(err));
			}
		} finally {
			setBusy(false);
		}
	};

	if (stage.kind === "starting") return <Text type="secondary">Preparing your authenticator setup…</Text>;

	if (stage.kind === "failed") {
		return (
			<div data-testid="security-totp-failed">
				<Alert type="error" showIcon message={stage.message} style={{ marginBottom: "var(--mg-sp-6)" }} />
				<Space wrap>
					<Button type="primary" onClick={() => void start()}>
						Start again
					</Button>
					{onCancel && <Button onClick={onCancel}>Cancel</Button>}
				</Space>
			</div>
		);
	}

	if (stage.kind === "codes") {
		return (
			<div>
				<RecoveryCodesList codes={stage.codes} />
				<Button data-testid="security-recovery-codes-saved" type="primary" onClick={continueAfterCodes} style={{ marginTop: "var(--mg-sp-7)" }}>
					I have saved these codes
				</Button>
			</div>
		);
	}

	if (stage.kind === "finish") {
		return (
			<StepUpForm
				methods={["totp"]}
				onSuccess={onDone}
				submitLabel="Finish setup"
				intro={
					<Paragraph data-testid="security-totp-finish">
						Authenticator app added. Wait for the next code to appear in your app (up to 30 seconds), then enter it to finish.
					</Paragraph>
				}
			/>
		);
	}

	const { enrolment } = stage;
	return (
		<div data-testid="security-totp-setup">
			<Paragraph>Scan this QR code with an authenticator app such as Google Authenticator, Microsoft Authenticator or 1Password.</Paragraph>
			<div className="security-qr">
				<QRCodeSVG data-testid="security-totp-qr" value={enrolment.otpauth_uri} size={176} level="M" marginSize={2} bgColor="#ffffff" fgColor="#000000" title="QR code for your authenticator app" />
			</div>
			<Paragraph type="secondary">
				Cannot scan it? Enter this key instead:{" "}
				<Text data-testid="security-totp-secret" code copyable={{ text: enrolment.secret, tooltips: ["Copy key", "Copied"] }}>
					{groupSecret(enrolment.secret)}
				</Text>
			</Paragraph>
			{error && <Alert data-testid="security-totp-error" type="error" showIcon message={error} style={{ marginBottom: "var(--mg-sp-6)" }} />}
			<Form form={form} name="totp-setup" layout="vertical" onFinish={(values) => confirm(enrolment, values)} requiredMark={false}>
				<Form.Item
					name="code"
					label="Code from the app"
					rules={[{ validator: (_, value) => (isOtp(value ?? "") ? Promise.resolve() : Promise.reject(new Error("Enter the 6-digit code"))) }]}
				>
					<OtpInput data-testid="security-totp-code" disabled={busy} />
				</Form.Item>
				{emailRequired && <EnrolmentEmailCode onNotRequired={() => setEmailRequired(false)} disabled={busy} />}
				<Space wrap>
					<Button data-testid="security-totp-confirm" type="primary" htmlType="submit" loading={busy}>
						Turn on
					</Button>
					{onCancel && (
						<Button onClick={onCancel} disabled={busy}>
							Cancel
						</Button>
					)}
				</Space>
			</Form>
		</div>
	);
};

export default TotpSetup;
